import { useState } from 'react'
import { t } from '@/i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  commitStatement,
  getAccounts,
  getTransaction,
  getTransactions,
  previewStatement,
  type StatementPreviewOut,
} from '@/lib/api'
import { formatDateTime, formatMoney } from '@/lib/format'

const STATEMENT_KEYS = [
  { key: 'booked_at', required: true },
  { key: 'value_date' },
  { key: 'amount' },
  { key: 'debit' },
  { key: 'credit' },
  { key: 'direction' },
  { key: 'currency' },
  { key: 'payer_name' },
  { key: 'payer_account' },
  { key: 'description' },
  { key: 'reference' },
  { key: 'external_id' },
  { key: 'balance_after' },
] as const

const RECON_KEY: Record<string, string> = {
  pending: 'tx.rs.pending',
  matched: 'tx.rs.matched',
  unmatched: 'tx.rs.unmatched',
  partial: 'tx.rs.partial',
  conflict: 'tx.rs.conflict',
  review: 'tx.rs.review',
  reversed: 'tx.rs.reversed',
}

function reconLabel(status: string): string {
  const key = RECON_KEY[status]
  return key ? t(key) : status
}

export function Transactions() {
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [accountId, setAccountId] = useState('')
  const [direction, setDirection] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const bankAccounts = (accounts.data ?? []).filter((item) => item.kind === 'bank')
  const list = useQuery({
    queryKey: ['transactions', q, accountId, direction],
    queryFn: () =>
      getTransactions({
        q: q || undefined,
        account_id: accountId || undefined,
        direction: direction || undefined,
      }),
  })

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('tx.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('tx.lead')}</p>
        </div>
        <button
          type="button"
          className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900"
          onClick={() => setImportOpen(true)}
        >
          {t('tx.import')}
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder={t('tx.search')}
          className="w-72 rounded-md border border-surface-border bg-surface px-3 py-1.5 text-sm"
        />
        <select
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
          className="rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
        >
          <option value="">{t('tx.allAcc')}</option>
          {bankAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>
        <select
          value={direction}
          onChange={(event) => setDirection(event.target.value)}
          className="rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
        >
          <option value="">{t('tx.allDir')}</option>
          <option value="in">{t('rec.in')}</option>
          <option value="out">{t('rec.out')}</option>
        </select>
      </div>

      {list.isLoading && <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>}
      {list.isError && (
        <p className="mt-6 text-sm text-loss">{list.error instanceof Error ? list.error.message : t('common.error')}</p>
      )}
      {list.data && list.data.items.length === 0 && (
        <p className="mt-6 text-sm text-slate-500">{t('tx.empty')}</p>
      )}

      {list.data && list.data.items.length > 0 && (
        <table className="mt-6 w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="py-2">{t('tx.date')}</th>
              <th className="py-2">{t('tx.account')}</th>
              <th className="py-2">{t('tx.payer')}</th>
              <th className="py-2">{t('tx.purpose')}</th>
              <th className="py-2 text-right">{t('tx.amount')}</th>
              <th className="py-2">{t('tx.recon')}</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-t border-surface-border/60 hover:bg-slate-800/40"
                onClick={() => setSelectedId(row.id)}
              >
                <td className="py-2 text-slate-400">{formatDateTime(row.booked_at)}</td>
                <td className="py-2">{row.account_name ?? '—'}</td>
                <td className="py-2">{row.payer_name || row.payer_display_hint || '—'}</td>
                <td className="max-w-xs truncate py-2 text-slate-400">{row.description || row.reference || '—'}</td>
                <td
                  className={[
                    'num py-2 text-right',
                    row.direction === 'in' ? 'text-profit' : 'text-loss',
                  ].join(' ')}
                >
                  {row.direction === 'in' ? '+' : '−'}
                  {formatMoney(row.amount, row.currency_code)}
                </td>
                <td className="py-2 text-slate-500">{reconLabel(row.recon_status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedId && <TransactionCard id={selectedId} onClose={() => setSelectedId(null)} />}
      {importOpen && (
        <StatementImport
          accounts={bankAccounts.map((item) => ({ id: item.id, name: item.name }))}
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false)
            void queryClient.invalidateQueries({ queryKey: ['transactions'] })
            void queryClient.invalidateQueries({ queryKey: ['freshness'] })
          }}
        />
      )}
    </div>
  )
}

function TransactionCard({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: ['transactions', id], queryFn: () => getTransaction(id) })
  const row = detail.data
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg border border-surface-border bg-surface-raised p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-base font-semibold text-slate-100">{t('tx.card')}</h2>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-200" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        {detail.isLoading && <p className="mt-4 text-sm text-slate-500">{t('common.loading')}</p>}
        {row && (
          <dl className="mt-4 space-y-2 text-sm">
            <Row label={t('tx.date')} value={formatDateTime(row.booked_at)} />
            <Row label={t('tx.valueDate')} value={row.value_date ?? '—'} />
            <Row label={t('tx.account')} value={row.account_name ?? '—'} />
            <Row
              label={t('tx.amount')}
              value={`${row.direction === 'in' ? '+' : '−'}${formatMoney(row.amount, row.currency_code)}`}
            />
            <Row label={t('tx.payer')} value={row.payer_name || row.payer_display_hint || '—'} />
            <Row label={t('tx.payerAcc')} value={row.payer_account || '—'} />
            <Row label={t('tx.purpose')} value={row.description || '—'} />
            <Row label={t('tx.ref')} value={row.reference || '—'} />
            <Row label={t('tx.bankId')} value={row.external_id || '—'} />
            <Row label={t('tx.recon')} value={reconLabel(row.recon_status)} />
          </dl>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-surface-border/50 pb-1.5 last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-200">{value}</dd>
    </div>
  )
}

function StatementImport({
  accounts,
  onClose,
  onDone,
}: {
  accounts: { id: string; name: string }[]
  onClose: () => void
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<StatementPreviewOut | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '')
  const [result, setResult] = useState<string | null>(null)
  const [pickError, setPickError] = useState<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: (next: File) => previewStatement(next),
    onSuccess: (data) => {
      setPreview(data)
      setMapping(data.mapping)
      setResult(null)
    },
  })

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!file || !preview) throw new Error(t('tx.needFile'))
      if (!accountId) throw new Error(t('tx.needAcc'))
      return commitStatement({
        file,
        account_id: accountId,
        delimiter: preview.delimiter,
        mapping,
        skip_duplicates: true,
      })
    },
    onSuccess: (data) => {
      if (data.extraction_job_id) {
        setResult(t('tx.pdfQueued'))
        return
      }
      setResult(
        t('tx.written', { c: data.created, d: data.skipped_duplicates, f: data.failed }),
      )
      if (data.created > 0 || data.skipped_duplicates > 0) onDone()
    },
  })

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-auto bg-black/60 px-4 py-8">
      <div className="w-full max-w-4xl rounded-lg border border-surface-border bg-surface-raised p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-100">{t('tx.import')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('tx.importLead')}</p>
          </div>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-200" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-400">
            {t('tx.account')}
            <select
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              className="mt-1 w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-sm"
            >
              {accounts.length === 0 && <option value="">{t('tx.needBankFirst')}</option>}
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            {t('tx.file')}
            <input
              type="file"
              accept=".csv,.txt,.xlsx,.xlsm,.sta,.940,.mt940,.pdf,text/csv,application/pdf"
              className="mt-1 block w-full text-sm"
              onChange={(event) => {
                const next = event.target.files?.[0]
                if (!next) return
                const name = next.name.toLowerCase()
                if (/\.(jpe?g|png|webp|gif|heic|bmp|svg)$/.test(name)) {
                  setFile(null)
                  setPreview(null)
                  setPickError(t('tx.imgErr'))
                  previewMutation.reset()
                  return
                }
                setPickError(null)
                setFile(next)
                previewMutation.mutate(next)
              }}
            />
          </label>
        </div>

        {(pickError || previewMutation.isError) && (
          <p className="mt-3 text-sm text-loss">
            {pickError ??
              (previewMutation.error instanceof Error
                ? previewMutation.error.message
                : t('imp.readFail'))}
          </p>
        )}

        {preview && (
          <>
            <p className="mt-4 text-xs text-slate-500">
              {t('tx.kind')} <span className="text-slate-300">{preview.kind}</span>
              {preview.bank_hint ? t('tx.bankHint', { b: preview.bank_hint }) : ''}
              {t('tx.rowHint', { n: preview.row_count })}
            </p>
            {preview.issues.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm text-pending">
                {preview.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}

            {preview.kind !== 'mt940' && preview.headers.length > 0 && (
              <>
                <h3 className="mt-5 text-sm font-medium text-slate-300">{t('imp.map')}</h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {STATEMENT_KEYS.map((field) => (
                    <label key={field.key} className="text-xs text-slate-400">
                      {t(`tx.f.${field.key}`)}
                      {'required' in field && field.required ? ' *' : ''}
                      <select
                        value={mapping[field.key] ?? ''}
                        onChange={(event) =>
                          setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))
                        }
                        className="mt-1 w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
                      >
                        <option value="">{t('imp.unmap')}</option>
                        {preview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </>
            )}

            <h3 className="mt-5 text-sm font-medium text-slate-300">{t('imp.sample')}</h3>
            <div className="mt-2 overflow-auto rounded-md border border-surface-border">
              <table className="min-w-full text-xs">
                <thead className="text-left text-slate-500">
                  <tr>
                    {(preview.headers.length ? preview.headers : [t('tx.date'), t('tx.amount'), t('tx.payer')]).slice(0, 8).map((header) => (
                      <th key={header} className="px-2 py-1">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.slice(0, 8).map((row, index) => (
                    <tr key={index} className="border-t border-surface-border/60">
                      {(preview.headers.length ? preview.headers : ['booked_at', 'amount', 'payer_name'])
                        .slice(0, 8)
                        .map((header) => (
                          <td key={header} className="px-2 py-1 text-slate-300">
                            {String(
                              (row._raw as Record<string, string> | undefined)?.[header] ??
                                (row as Record<string, unknown>)[header] ??
                                '',
                            )}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="mt-5 rounded-md bg-slate-100 px-4 py-2 text-sm text-slate-900 disabled:opacity-50"
              disabled={commitMutation.isPending || !accountId}
              onClick={() => commitMutation.mutate()}
            >
              {commitMutation.isPending ? t('imp.going') : t('imp.go')}
            </button>
            {commitMutation.isError && (
              <p className="mt-3 text-sm text-loss">
                {commitMutation.error instanceof Error ? commitMutation.error.message : t('imp.fail')}
              </p>
            )}
            {result && <p className="mt-3 text-sm text-profit">{result}</p>}
            {commitMutation.data && commitMutation.data.errors.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-xs text-loss">
                {commitMutation.data.errors.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
